import {forwardRef, ReactElement, Ref, useCallback, useEffect, useRef, useState} from 'react';
import './senior-tasks.daily-care-log.screen.scss';
import {useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {
  Avatar,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import {Pallete} from '../../../../theme/bluebird';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import {CareTaskComplementary} from '../../../../backend/interfaces/care-task-complementary';
import {EntityUser} from '../../../../backend/responses/entity-user';
import {EntityUsersRepository} from '../../../../repositories/entity-users';
import TaskTable from '../../../../components/daily-care-log/task-table/task-table';
import {EntityShiftsRepository} from '../../../../repositories/entity-shifts';
import {EntityUserCareTasksRepository} from '../../../../repositories/entity-user-care-tasks';
import {useEntity} from '../../../../contexts/entity/context';
import {useShiftDetailsSearchParams} from '../shift-details/use-shift-search-params';
import {CareTask} from '../../../../backend/interfaces/care-task';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {useAuth} from '../../../../contexts/auth/context';
import {useSeniorProfilePdfExport} from '../../../../hooks/use-senior-profile-pdf-export';
import {TransitionProps} from '@mui/material/transitions';

const avatarSx: SxProps<Theme> = {
  background: Pallete.gray.lightbg,
  color: Pallete.gray.light,
  width: '36px',
  height: '36px',
  marginTop: 0,
  aspectRatio: '1/1',
  fontSize: '50px',
  fontWeight: '600',
  lineHeight: '26px',
};

const seniorNameSx: SxProps<Theme> = {
  color: 'var(--dcm-gray)',
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '22px',
  letterSpacing: '-0.408px',
  transition: 'color 150ms ease',
};

type LocationState = {
  tasks: CareTaskComplementary[];
};

const BottomSheetTransition = forwardRef(function BottomSheetTransition(
  props: TransitionProps & {children: ReactElement<any, any>},
  ref: Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function SeniorTasksDailyCareLogScreen() {
  const [senior, setSenior] = useState<EntityUser | undefined>(undefined);
  const [entitySeniors, setEntitySeniors] = useState<Map<string, EntityUser> | undefined>(
    undefined
  );
  const [careTasks, setCareTasks] = useState<Array<CareTaskComplementary> | undefined>(undefined);
  let {entityId, userId}: {entityId: string; userId: string} = useParams();
  const dailyCareLogContext = useDailyCareLog();
  const location = useLocation();
  const {t} = useTranslation();
  const {state: entityState} = useEntity();
  const authContext = useAuth();
  const {handleExportPdf} = useSeniorProfilePdfExport({
    errorNotificationChannel: 'daily-care-log',
  });
  const {date, startDate, endDate, entityShiftId, careServiceUserId} =
    useShiftDetailsSearchParams();
  const [actionsOpen, setActionsOpen] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const isGeneratingPdfRef = useRef(false);
  const pendingPdfExportRef = useRef<{
    seniorId: string;
    entity: EntityInfo;
  } | null>(null);
  const selectedEntity = authContext.state.session?.selectedAffiliation?.entity;
  const currentEntity = selectedEntity?.id === entityId ? selectedEntity : undefined;
  const seniorDisplayName = senior?.user.displayName ?? senior?.user.name ?? '';
  const canOpenActions = Boolean(senior?.user.id && currentEntity);

  const fetchSeniorCareTasks = useCallback(async () => {
    if (!entityShiftId) {
      EntityUserCareTasksRepository.instance
        .GetCareTasksWithComplementaryInformation(
          entityId,
          userId,
          startDate.utc().toISOString(),
          endDate.utc().toISOString()
        )
        .then((response) => {
          setCareTasks(response.data);
        })
        .catch((error) => {
          console.error(`🔴 Erro: ${error}`);
        });
    } else {
      let prom: Promise<CareTaskComplementary[] | CareTask[]>;
      if (careServiceUserId) {
        prom = EntityUserCareTasksRepository.instance.GetEntityCareTasks(
          entityId,
          startDate.utc().toISOString(),
          endDate.utc().toISOString()
        );
      } else {
        prom = EntityShiftsRepository.instance.getShiftCareTasks(
          entityId,
          entityShiftId,
          startDate.utc().toISOString(),
          endDate.utc().toISOString(),
          true
        );
      }

      prom
        .then((caretasks) => {
          let tasks = caretasks as CareTaskComplementary[];
          setCareTasks(tasks.filter((task) => task.careTask.userId === userId));
        })
        .catch((error) => {
          console.error(`🔴 Erro: ${error}`);
        });
    }
  }, [startDate, endDate, entityId, entityShiftId, userId, careServiceUserId]);

  const fetchPageData = useCallback(async () => {
    EntityUsersRepository.instance.getById(entityId, userId).then((senior) => {
      setSenior(senior);

      const seniors = new Map<string, EntityUser>();
      seniors.set(senior.user.id!, senior);
      setEntitySeniors(seniors);
    });
  }, [entityId, userId]);

  useEffect(() => {
    if (location.state) {
      setCareTasks((location.state as LocationState).tasks);

      // Clear location.state so that it's not reused on getting back to this screen from the edit page
      window.history.replaceState(null, '');
    } else {
      fetchSeniorCareTasks();
    }
  }, [fetchSeniorCareTasks, location.state]);

  useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const handleOpenActions = () => {
    if (!canOpenActions || isGeneratingPdfRef.current) return;
    setActionsOpen(true);
  };

  const handleSelectPdf = () => {
    const seniorId = senior?.user.id;
    if (!seniorId || !currentEntity || isGeneratingPdfRef.current) return;

    isGeneratingPdfRef.current = true;
    setIsGeneratingPdf(true);
    pendingPdfExportRef.current = {seniorId, entity: currentEntity};
    setActionsOpen(false);
  };

  const handleActionsSheetExited = () => {
    const pendingExport = pendingPdfExportRef.current;
    if (!pendingExport) return;

    pendingPdfExportRef.current = null;
    window.setTimeout(async () => {
      try {
        await handleExportPdf(pendingExport);
      } finally {
        isGeneratingPdfRef.current = false;
        setIsGeneratingPdf(false);
      }
    }, 0);
  };

  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(true, t(translations.daily_care_log.generics.back));
    dailyCareLogContext.toggleBottomNavigation(true);

    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack sx={{mt: 1}} gap={1}>
      {/*
       * User header
       */}
      <ButtonBase
        component="button"
        type="button"
        aria-label={t(translations.daily_care_log.senior_actions.open_actions, {
          name: seniorDisplayName,
        })}
        aria-haspopup="dialog"
        aria-expanded={actionsOpen}
        aria-disabled={isGeneratingPdf || !canOpenActions}
        disabled={!canOpenActions}
        onClick={handleOpenActions}
        sx={{
          minHeight: '44px',
          width: 'fit-content',
          maxWidth: '100%',
          px: 1,
          py: 0.5,
          justifyContent: 'flex-start',
          gap: 1,
          borderRadius: 1,
          textAlign: 'left',
          transition: 'background-color 150ms ease',
          '@media (hover: hover) and (pointer: fine)': {
            '&:hover': {backgroundColor: Pallete.gray.lightbg},
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${Pallete.blue.base}`,
            outlineOffset: 2,
            backgroundColor: Pallete.gray.lightbg,
          },
          '&[aria-disabled="true"]:not(:disabled)': {
            cursor: 'not-allowed',
            opacity: 0.7,
          },
        }}
      >
        <Avatar
          alt={senior?.user.name}
          src={senior?.user.picture}
          sx={{
            ...avatarSx,
            flexShrink: 0,
            border: `1px solid ${
              entityState.userEntitySocialResponses[0]?.foregroundColor
                ? entityState.userEntitySocialResponses[0].foregroundColor
                : 'transparent'
            }`,
          }}
        />
        <Typography sx={{...seniorNameSx, minWidth: 0, flex: '0 1 auto'}} noWrap>
          {seniorDisplayName}
        </Typography>
      </ButtonBase>

      <Dialog
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        onTransitionExited={handleActionsSheetExited}
        TransitionComponent={BottomSheetTransition}
        fullWidth
        maxWidth="sm"
        aria-labelledby="senior-actions-title"
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-end',
            justifyContent: 'center',
          },
          '& .MuiDialog-paper': {
            m: 0,
            width: {xs: '100%', sm: 'min(100%, 480px)'},
            maxWidth: 'none',
            maxHeight: 'min(80vh, 480px)',
            borderRadius: '16px 16px 0 0',
          },
        }}
      >
        <DialogTitle
          id="senior-actions-title"
          component="h2"
          sx={{
            px: 2.5,
            pt: 2.5,
            pb: 1.5,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          {t(translations.daily_care_log.senior_actions.title, {name: seniorDisplayName})}
        </DialogTitle>
        <DialogContent sx={{px: 1.5, pb: 2.5}}>
          <ButtonBase
            component="button"
            type="button"
            aria-label={t(translations.export.senior_profile.save_pdf)}
            disabled={isGeneratingPdf}
            onClick={handleSelectPdf}
            sx={{
              width: '100%',
              minHeight: '56px',
              justifyContent: 'flex-start',
              gap: 1.5,
              px: 1.5,
              borderRadius: 1,
              textAlign: 'left',
              '&:hover': {backgroundColor: Pallete.blue.lighter},
              '&.Mui-focusVisible': {
                outline: `2px solid ${Pallete.blue.base}`,
                outlineOffset: -2,
              },
            }}
          >
            <DescriptionIcon aria-hidden="true" sx={{color: Pallete.blue.base, flexShrink: 0}} />
            <Typography component="span">
              {t(translations.export.senior_profile.save_pdf)}
            </Typography>
          </ButtonBase>
        </DialogContent>
      </Dialog>

      {/*
       * Task table
       */}
      <TaskTable
        tasks={careTasks}
        seniors={entitySeniors}
        entitySocialResponses={entityState.userEntitySocialResponses}
        hideAvatar
        entityShiftId={entityShiftId || ''}
        date={date.format('YYYY-MM-DD')}
        defaultPatientId={userId}
      />
    </Stack>
  );
}
